<template>
	<div
		v-if="items.length"
		class="c-search-dropdown"
		:class="{
			'c-search-dropdown--is-expanded': isExpanded,
		}"
	>
		<DropdownButton
			:id="computedId"
			ref="button"
			v-slot="{ selectedValues, selectedTexts }"
			v-bind="$attrs"
			v-model="internalValue"
			:aria-labelledby="computedId"
			:aria-owns="`c-search-dropdown__list-${_uid} c-search-dropdown__button-up-${_uid} c-search-dropdown__button-down-${_uid}`"
			:aria-required="String(required)"
			class="
				flex-shrink flex-grow flex
				justify-start
				items-center
				w-full
				h-full
				pl-sm/h
				pr-xs/h
				py-3xs/v
				rounded-full
			"
			:multiple="multiple"
			@state:toggle="onStateToggle"
			v-on="computedListeners"
		>
			<div
				v-if="selectedValues.length"
				class="
					w-28
					h-28
					>=768:-ml-14
					flex
					justify-center
					items-center
					text-body-xs
					font-bold
					rounded-full
					mr-4xs/h
				"
				:class="{
					'bg-theme text-white': isExpanded,
					'bg-white text-theme': !isExpanded,
				}"
				:aria-label="
					[`${selectedValues.length} valgt`, selectedTexts.join(', ')]
						.filter(Boolean)
						.join(': ')
				"
			>
				{{ selectedValues.length }}
			</div>

			<span
				class="inline-flex items-center min-h-28"
				v-text="computedPlaceholder"
			></span>

			<SvgIconWedge
				:class="[
					'flex-shrink-0 flex-grow-0',
					'ml-auto w-12 h-8 mt-1',
					{ 'transform -rotate-180 -mt-1': isExpanded },
				]"
			/>
		</DropdownButton>
		<input type="hidden" :name="name" :value="String(internalValue)" />

		<span
			v-if="computedLabelId"
			:id="computedLabelId"
			class="hidden"
			v-text="label"
		></span>

		<div
			ref="scrollShade"
			class="
				c-search-dropdown__list-wrap
				absolute
				top-full
				-left-1
				-right-1
				rounded-b-30
				overflow-hidden
			"
			:style="{
				'--scroll-shade-alpha-top': scrollShadeAlphaTop,
				'--scroll-shade-alpha-bottom': scrollShadeAlphaBottom,
			}"
		>
			<DropdownOptionList
				:id="`c-search-dropdown__list-${_uid}`"
				v-slot="{
					activedescendant,
					selectedValues,
					isNavigatingByKeyboard,
				}"
				class="
					relative
					pt-5xs/v
					pb-xs/v
					bg-white
					text-theme text-body-sm
					rounded-b-30
					overflow-auto
					overscroll-auto
				"
				style="max-height: min(422px, 50vh)"
			>
				<DropdownOption
					v-for="(item, itemIndex) in items.filter(
						(item) => item.value
					)"
					:id="`c-search-dropdown__option-${_uid}-${item.value}`"
					:key="`${_uid}-${item.value}`"
					:value="item.value"
					class="
						flex
						items-center
						pl-sm/h
						pr-xs/h
						py-5xs/v
						snap-my-sm/v
					"
					:class="{
						'>=768:mt-3xs/v': showIcon && itemIndex,
						'hover:bg-theme-20 hover:bg-opacity-20': !(
							isNavigatingByKeyboard &&
							`c-search-dropdown__option-${_uid}-${item.value}` ===
								activedescendant
						),
						'bg-theme text-white hover:bg-theme-90':
							isNavigatingByKeyboard &&
							`c-search-dropdown__option-${_uid}-${item.value}` ===
								activedescendant,
					}"
				>
					<template v-if="showIcon">
						<AreaIcon
							class="
								flex-shrink-0 flex-grow-0
								w-30
								h-30
								mr-16
								>=768:mr-20
							"
							:src="item.iconUrl"
							:invert="
								!!(
									isNavigatingByKeyboard &&
									`c-search-dropdown__option-${_uid}-${item.value}` ===
										activedescendant
								)
							"
						/>
					</template>

					<span>{{ item.label }}</span>

					<template v-if="multiple">
						<div
							style="border-radius: 2px"
							class="
								flex-shrink-0 flex-grow-0 flex
								justify-center
								items-center
								ml-auto
								w-20
								h-20
								border-2 border-current
							"
							:class="{
								'bg-current':
									selectedValues &&
									selectedValues.includes(item.value),
								'text-theme': !(
									isNavigatingByKeyboard &&
									`c-search-dropdown__option-${_uid}-${item.value}` ===
										activedescendant
								),
								'text-white':
									isNavigatingByKeyboard &&
									`c-search-dropdown__option-${_uid}-${item.value}` ===
										activedescendant,
							}"
						>
							<SvgIconCheckmark
								v-if="
									selectedValues &&
									selectedValues.includes(item.value)
								"
								class="w-12 h-12"
								:class="{
									'text-white': !(
										isNavigatingByKeyboard &&
										`c-search-dropdown__option-${_uid}-${item.value}` ===
											activedescendant
									),
									'text-theme':
										isNavigatingByKeyboard &&
										`c-search-dropdown__option-${_uid}-${item.value}` ===
											activedescendant,
								}"
							/>
						</div>
					</template>
				</DropdownOption>
			</DropdownOptionList>

			<!-- Arrow icons -->
			<TransitionExt name="fade-in-out" duration="200">
				<button
					v-if="scrollShadeAlphaTop"
					:id="`c-search-dropdown__button-up-${_uid}`"
					class="
						absolute
						z-10
						top-0
						left-1/2
						w-52
						h-52
						transform
						-translate-x-1/2
						flex
						justify-center
						items-center
						transition
						duration-200
						ease-in-out
						bg-theme-20
						hover:bg-theme-10
						rounded-full
					"
					@mousedown="startScrollUp"
					@touchstart="startScrollUp"
				>
					<SvgIconWedge
						class="transform rotate-180 text-gray-darker w-16 h-10"
					/>
				</button>
			</TransitionExt>
			<TransitionExt name="fade-in-out" duration="200">
				<button
					v-if="scrollShadeAlphaBottom"
					:id="`c-search-dropdown__button-down-${_uid}`"
					class="
						absolute
						z-10
						bottom-16
						left-1/2
						w-52
						h-52
						transform
						-translate-x-1/2
						flex
						justify-center
						items-center
						transition
						duration-200
						ease-in-out
						bg-theme-20
						hover:bg-theme-10
						rounded-full
					"
					@mousedown="startScrollDown"
					@touchstart="startScrollDown"
				>
					<SvgIconWedge class="text-gray-darker w-16 h-10" />
				</button>
			</TransitionExt>
		</div>
	</div>
</template>

<script>
import {
	DropdownButton,
	DropdownOption,
	DropdownOptionList,
} from '~/components/shared/Dropdown';
import AreaIcon from '~/components/main/AreaIcon';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg?inline';
import SvgIconCheckmark from '~/assets/svgs/icon-checkmark.svg?inline';

export default {
	name: 'SearchDropdown',

	components: {
		DropdownButton,
		DropdownOption,
		DropdownOptionList,
		AreaIcon,
		SvgIconWedge,
		SvgIconCheckmark,
	},
	inheritAttrs: false,

	props: {
		id: {
			type: String,
			default: '',
		},

		type: {
			type: String,
			default: 'dropdown',
		},

		name: {
			type: String,
			default: '',
		},

		value: {
			type: String,
			default: '',
		},

		required: {
			type: Boolean,
			default: false,
		},

		items: {
			type: Array,
			default: () => [],
		},

		placeholder: {
			type: String,
			default: '',
		},

		label: {
			type: String,
			default: '',
		},

		multiple: {
			type: Boolean,
			default: true,
		},

		showIcon: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			isExpanded: false,
			internalValue: this.value,

			resizeObserver: null,
			animationFrame: null,
			scrollShadeAlphaTop: 0,
			scrollShadeAlphaBottom: 0,
		};
	},

	computed: {
		computedId() {
			return this.id || `c-search-dropdown-${this._uid}`;
		},

		computedLabelId() {
			if (!this.internalValue && this.label === this.placeholder) {
				return null;
			}
			return this.label ? `${this.computedId}__label` : null;
		},

		computedListeners() {
			const listeners = {
				...this.$listeners,
			};
			delete listeners.input;
			return listeners;
		},

		computedPlaceholder() {
			return (
				this.placeholder ||
				this.items.find((item) => !item.value)?.label ||
				''
			);
		},
	},

	watch: {
		value(value) {
			this.internalValue = value;
			this.updateCheckedItems();
		},
		internalValue(value) {
			this.$emit('input', value);
			this.updateCheckedItems();
		},
	},

	mounted() {
		// Detect if a scroll shadow should be present
		const { scrollShade } = this.$refs;
		if (scrollShade) {
			this.resizeObserver = new ResizeObserver(() => {
				this.updateScrollShade();
			});
			this.resizeObserver.observe(scrollShade);
			scrollShade.addEventListener(
				'scroll',
				this.updateScrollShade,
				true
			);
		}
	},

	beforeDestroy() {
		const { scrollShade } = this.$refs;
		this.resizeObserver?.disconnect?.();
		scrollShade?.removeEventListener?.(
			'scroll',
			this.updateScrollShade,
			true
		);
	},

	methods: {
		updateCheckedItems() {
			this.items.forEach((item) => {
				this.$set(item, 'checked', false);
			});
			this.internalValue.split(',').forEach((value) => {
				const item = this.items.find((item) => item.value === value);
				if (item) {
					this.$set(item, 'checked', true);
				}
			});
		},
		updateScrollShade() {
			window.cancelAnimationFrame(this.animationFrame);
			this.animationFrame = window.requestAnimationFrame(() => {
				const { scrollShade } = this.$refs;
				const container = scrollShade?.firstChild;
				if (container) {
					const { scrollTop, scrollHeight, clientHeight } = container;
					const scrollBottom =
						scrollHeight - clientHeight - scrollTop;
					const scrollShadeAlphaTop = Math.min(
						1,
						Math.max(0, scrollTop / 60)
					);
					const scrollShadeAlphaBottom = Math.min(
						1,
						Math.max(0, scrollBottom / 60)
					);
					if (
						this.scrollShadeAlphaTop !== scrollShadeAlphaTop ||
						this.scrollShadeAlphaBottom !== scrollShadeAlphaBottom
					) {
						this.scrollShadeAlphaTop = scrollShadeAlphaTop;
						this.scrollShadeAlphaBottom = scrollShadeAlphaBottom;
					}
				}
			});
		},
		onStateToggle(event) {
			this.isExpanded = event;
			if (event) {
				this.$el.scrollIntoView();
			}
		},
		startScrollUp() {
			const scrollAmount = 10;
			const list = this.$el.querySelector(
				`#c-search-dropdown__list-${this._uid}`
			);
			let scrollMultiplier = 1;
			let scrolls = 0;

			let isScrolling = true;
			const stopScroll = (e) => {
				isScrolling = false;
				window.removeEventListener('mouseup', stopScroll, true);
				window.removeEventListener('touchend', stopScroll, true);

				if (e && scrolls < 5 && list) {
					list.scrollTop -= Math.max(
						0,
						list.offsetHeight - 150 - scrolls * scrollAmount
					);
				}
			};
			window.addEventListener('mouseup', stopScroll, true);
			window.addEventListener('touchend', stopScroll, true);

			const scrollUp = () => {
				if (!this.scrollShadeAlphaTop || !list) {
					stopScroll();
				}

				if (isScrolling) {
					scrolls++;
					list.scrollTop -= scrollAmount * scrollMultiplier;

					scrollMultiplier = Math.min(10, scrollMultiplier * 1.02);
					window.setTimeout(scrollUp, 30);
				}
			};
			scrollUp();
		},
		startScrollDown() {
			const scrollAmount = 10;
			const list = this.$el.querySelector(
				`#c-search-dropdown__list-${this._uid}`
			);
			let scrollMultiplier = 1;
			let scrolls = 0;

			let isScrolling = true;
			const stopScroll = (e) => {
				isScrolling = false;
				window.removeEventListener('mouseup', stopScroll, true);
				window.removeEventListener('touchend', stopScroll, true);

				if (e && scrolls < 5 && list) {
					list.scrollTop += Math.max(
						0,
						list.offsetHeight - 150 - scrolls * scrollAmount
					);
				}
			};
			window.addEventListener('mouseup', stopScroll, true);
			window.addEventListener('touchend', stopScroll, true);

			const scrollDown = () => {
				if (!this.scrollShadeAlphaBottom || !list) {
					stopScroll();
				}

				if (isScrolling) {
					scrolls++;
					list.scrollTop += scrollAmount * scrollMultiplier;

					scrollMultiplier = Math.min(10, scrollMultiplier * 1.02);
					window.setTimeout(scrollDown, 30);
				}
			};
			scrollDown();
		},
	},
};
</script>

<style lang="postcss">
.c-search-dropdown {
	@apply relative w-full h-auto text-input text-white border border-white rounded-full;

	&.c-search-dropdown--is-expanded {
		@apply z-10 bg-white text-theme rounded-30 rounded-b-none;
		filter: drop-shadow(0px 4px 24px rgba(var(--theme-colors-theme), 0.16));
	}

	& > button {
		min-height: min(100%, 52px);
	}
}

.c-search-dropdown__list-wrap {
	@apply relative;

	&:after {
		@apply rounded-b-30;
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(
				rgba(255, 255, 255, var(--scroll-shade-alpha-top, 0)) 24%,
				rgba(255, 255, 255, 0)
			),
			linear-gradient(
					rgba(255, 255, 255, 0),
					rgba(255, 255, 255, var(--scroll-shade-alpha-bottom, 0)) 76%
				)
				0 100%;
		background-repeat: no-repeat;
		background-attachment: local, local;
		background-size: 100% 92px, 100% 100px;
		pointer-events: none;
	}
}

@screen >=768 {
	.c-search-dropdown > button {
		min-height: min(100%, 60px);
	}
}
</style>
